import { urlRegexWithTrackingParams } from './UrlRegex'

export type UrlRedirectCheckWarningReason = 'httpError' | 'expiredCertificate' | 'requestFailed'

export type UrlRedirectCheckOutcome =
  | { kind: 'none' }
  | { kind: 'info'; finalUrl?: string }
  | { kind: 'warning'; reason: UrlRedirectCheckWarningReason; httpStatus?: number }

export function isExpiredCertificateError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  const upperMessage = message.toUpperCase()

  return (
    upperMessage.includes('SEC_ERROR_EXPIRED_CERTIFICATE') ||
    upperMessage.includes('ERR_CERT_DATE_INVALID') ||
    /certificate has expired/i.test(message) ||
    /expired certificate/i.test(message)
  )
}

export function resolveUrlForRedirectCheck(value: string): string | null {
  const trimmed = value.trim()
  if (!trimmed) {
    return null
  }
  if (urlRegexWithTrackingParams.test(trimmed)) {
    return trimmed
  }
  if (trimmed.includes('://')) {
    return null
  }
  const withHttps = `https://${trimmed.replace(/\/+$/, '')}/`
  if (urlRegexWithTrackingParams.test(withHttps)) {
    return withHttps
  }
  return null
}

export function isCheckableUrl(value: string): boolean {
  return resolveUrlForRedirectCheck(value) !== null
}

export function urlsEquivalentIgnoringTrailingSlash(a: string, b: string): boolean {
  try {
    return normalizeUrlForRedirectCompare(a) === normalizeUrlForRedirectCompare(b)
  } catch {
    return a === b
  }
}

function normalizeUrlForRedirectCompare(url: string): string {
  const parsed = new URL(url)
  let pathname = parsed.pathname
  if (pathname.length > 1 && pathname.endsWith('/')) {
    pathname = pathname.slice(0, -1)
  }
  return `${parsed.protocol}//${parsed.host}${pathname}${parsed.search}${parsed.hash}`
}

export async function checkUrlRedirect(
  url: string,
  fetchImpl: typeof fetch,
  signal: AbortSignal
): Promise<UrlRedirectCheckOutcome> {
  const requestUrl = resolveUrlForRedirectCheck(url)
  if (!requestUrl) {
    return { kind: 'none' }
  }

  let response: Response
  try {
    response = await fetchImpl(requestUrl, {
      method: 'GET',
      redirect: 'follow',
      credentials: 'omit',
      signal,
    })
  } catch (error) {
    if (isExpiredCertificateError(error)) {
      return { kind: 'warning', reason: 'expiredCertificate' }
    }
    return { kind: 'warning', reason: 'requestFailed' }
  }

  if (response.status >= 400) {
    return { kind: 'warning', reason: 'httpError', httpStatus: response.status }
  }

  const finalUrl = response.url?.trim()
  if (finalUrl && !urlsEquivalentIgnoringTrailingSlash(requestUrl, finalUrl)) {
    return { kind: 'info', finalUrl }
  }

  return { kind: 'none' }
}
